import { CameraIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import imageCompression from 'browser-image-compression';
import React, { useCallback, useRef, useState } from 'react';
import { Avatar } from '../components/Avatar';
import { usePageTitle } from '../hooks/usePageTitle';
import NameModal from '../components/modals/NameModal';
import PasswordModal from '@odo/shared-ui/components/PasswordModal';
import { useToast } from '../providers/ToastProvider';
import { useUser } from '../providers/UserProvider';
import { updatePassword, updateUserName } from '../services/user';
import { uploadProfilePicture } from '../services/user-files';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const compressImage = (file: File): Promise<File> =>
    imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        fileType: 'image/jpeg'
    });

const Account: React.FC = () => {
    usePageTitle('Account');
    const { user, setProfilePictureUrl, setName } = useUser();
    const { showToast } = useToast();
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isNameModalOpen, setIsNameModalOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [securityExpanded, setSecurityExpanded] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleNameSave = useCallback(
        async (next: string) => {
            try {
                const { name } = await updateUserName(next);
                setName(name);
                setIsNameModalOpen(false);
                showToast('Name updated', 'success');
            } catch (error) {
                showToast('Failed to update name', 'error');
                // Re-throw so NameModal stays open with the user's typed value
                // preserved for retry.
                throw error;
            }
        },
        [setName, showToast]
    );

    const handlePasswordSave = async (currentPassword: string, newPassword: string) => {
        setIsUpdating(true);
        try {
            await updatePassword(currentPassword, newPassword);
            setIsPasswordModalOpen(false);
            showToast('Password updated successfully', 'success');
        } catch {
            showToast('Failed to update password', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handlePhotoSelect = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;

            if (!file.type.startsWith('image/')) {
                showToast('Please select an image file', 'error');
                return;
            }
            if (file.size > MAX_FILE_SIZE) {
                showToast('Image must be smaller than 5MB', 'error');
                return;
            }

            const previewUrl = URL.createObjectURL(file);
            setPhotoPreview(previewUrl);
            setIsUploading(true);

            try {
                const compressed = await compressImage(file);
                const newUrl = await uploadProfilePicture(compressed);
                setPhotoPreview(null);
                setProfilePictureUrl(newUrl);
                showToast('Profile picture updated', 'success');
            } catch {
                showToast('Failed to upload profile picture', 'error');
                setPhotoPreview(null);
            } finally {
                setIsUploading(false);
                URL.revokeObjectURL(previewUrl);
            }
        },
        [setProfilePictureUrl, showToast]
    );

    if (isUpdating) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full">
            <div className="flex justify-start items-start pt-12 px-8 pb-16 w-full">
                <div className="w-full bg-white rounded-xl shadow-sm p-8">
                    <div className="mb-8">
                        <div className="flex flex-col items-center gap-3 bg-gray-100 rounded-xl py-8">
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="relative group w-24 h-24 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                                disabled={isUploading}
                            >
                                <Avatar
                                    imageURL={photoPreview ?? undefined}
                                    className="w-24 h-24"
                                />
                                {isUploading ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                                        <div className="h-6 w-6 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                    </div>
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors rounded-full">
                                        <CameraIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                )}
                            </button>
                            <div className="text-center">
                                <p className="text-base font-semibold text-gray-800">
                                    {user?.name ?? 'User'}
                                </p>
                                <p className="text-sm text-gray-500">{user?.email ?? ''}</p>
                            </div>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handlePhotoSelect}
                        />
                    </div>

                    <div className="mt-8">
                        <h3 className="text-xl font-bold text-gray-800 mb-6">Settings</h3>
                        <div className="rounded-xl border border-gray-200 bg-white">
                            <button
                                onClick={() => setSecurityExpanded((v) => !v)}
                                className="w-full flex items-center justify-between px-5 py-4"
                            >
                                <div className="flex flex-col items-start">
                                    <h4 className="text-base font-semibold text-gray-700">
                                        Security
                                    </h4>
                                    <span className="text-xs text-gray-500">
                                        Manage your account credentials
                                    </span>
                                </div>
                                <ChevronDownIcon
                                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${securityExpanded ? 'rotate-180' : ''}`}
                                />
                            </button>
                            <div
                                className={`grid transition-all duration-300 ease-out ${securityExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                            >
                                <div className="overflow-hidden">
                                    <div className="px-5 pb-5">
                                        <div className="h-px bg-gray-200 mb-5" />
                                        <div className="flex flex-col gap-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-sm font-semibold text-[#6B7280]">
                                                        Name
                                                    </span>
                                                    <span className="text-gray-800">
                                                        {user?.name || '—'}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => setIsNameModalOpen(true)}
                                                    className="px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                                >
                                                    Edit name
                                                </button>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <span className="text-sm font-semibold text-[#6B7280]">
                                                    Email
                                                </span>
                                                <span className="text-gray-800">
                                                    {user?.email ?? ''}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-sm font-semibold text-[#6B7280]">
                                                        Password
                                                    </span>
                                                    <span className="text-gray-800">••••••••</span>
                                                </div>
                                                <button
                                                    onClick={() => setIsPasswordModalOpen(true)}
                                                    className="px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                                >
                                                    Set new password
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <PasswordModal
                isOpen={isPasswordModalOpen}
                onClose={() => setIsPasswordModalOpen(false)}
                onSave={handlePasswordSave}
            />
            <NameModal
                isOpen={isNameModalOpen}
                initialName={user?.name ?? ''}
                onClose={() => setIsNameModalOpen(false)}
                onSave={handleNameSave}
            />
        </div>
    );
};

export { Account };
